<template>
  <label
    ref="target"
    class="tw-block tw-relative tw-w-full"
    :for="inputId"
    :class="{ 'xs:tw-max-w-[12.5rem] xl:tw-max-w-56': !fullWidth }"
  >
    <base-input
      :id="inputId"
      :placeholder="placeholder || $t('search')"
      :label="label"
      :focus="focus"
      class="tw-z-2"
      :class="noMargin ? '' : 'tw-ml-2'"
      :model-value="
        useSearchText ? searchText : userSearch ? searchUserName : search
      "
      :input-loading="inputLoading"
      :errors="errors"
      :max="max"
      @click="results.length && open()"
      @input="(input) => updateInput(input)"
      @keydown="handleKeyPress"
    >
      <template #prepend>
        <font-awesome-icon icon="search" />
      </template>
      <template #append>
        <div v-if="status">
          <base-loading :status="status" />
        </div>
        <base-button
          v-else-if="!searchEmpty"
          basic-btn
          :disabled="searchEmpty"
          text-link-hover
          danger
          :class="
            searchEmpty
              ? 'mod-placeholder-input'
              : 'hover:tw-text-danger focus:tw-text-danger'
          "
          icon="times"
          @click="clear"
        />
      </template>
    </base-input>

    <ul
      v-if="results.length && show && search !== ''"
      class="tw-absolute tw--mt-1.5 tw-inset-x-0 tw-cursor-default tw-shadow-sm tw-text-sm tw-overflow-auto tw-max-h-60 tw-z-10 game-input-background-color tw-border-2 tw-border-[--primary-button-border-color] tw-border-t-0"
      :class="[noMargin ? '' : 'tw-ml-2']"
    >
      <li
        v-for="(item, index) in results"
        :key="index"
        class="tw-p-2 mod-input--pl hover:game-primary-text-hover hover:game-primary-button-color-hover"
        @click="select(item)"
      >
        <div
          v-if="item.thumb || item.searchSubtext"
          class="tw-flex tw-items-center tw-gap-3"
        >
          <img
            v-if="item.thumb"
            :src="item.thumb"
            :alt="item.name"
            class="tw-shrink-0 tw-w-16 tw-aspect-video tw-object-cover"
          />
          <div class="tw-min-w-0 tw-flex-1">
            <span class="tw-block">{{ item.name }}</span>
            <span
              v-if="item.searchSubtext"
              class="tw-block tw-text-xs tw-opacity-70 tw-truncate"
            >
              {{ item.searchSubtext }}
            </span>
          </div>
        </div>
        <span v-else>{{ item.name }}</span>
      </li>
    </ul>
  </label>
</template>

<script>
import { usePagination, useSearch, useToggle } from '@composables'
import { genHtmlId, isString, throttle } from '@helpers/utils.js'
import { computed, toRefs, watch, ref } from 'vue'
import { onClickOutside } from '@vueuse/core'

export default {
  props: {
    id: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
      default: null,
    },
    label: {
      type: String,
      default: '',
    },
    throttle: {
      type: Number,
      default: 1000,
    },
    status: {
      type: String,
      default: null,
    },
    results: {
      type: Array,
      default: () => [],
    },
    noMargin: {
      type: Boolean,
      default: false,
    },
    errors: {
      type: Array,
      default: () => [],
    },
    inputLoading: {
      type: Boolean,
      default: false,
    },
    max: {
      type: Number,
      default: 0,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    useSearchText: {
      type: Boolean,
      default: false,
    },
    userSearch: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update', 'selected:item'],
  setup(props, { emit }) {
    const { id, throttle: throttleProp, results, userSearch } = toRefs(props)
    const {
      clearUserSearch,
      searchUserName,
      setUserSearch,
      clearSearch,
      searchText,
      setSearch,
      search,
    } = useSearch(id.value)

    const { resetPagination } = usePagination(id.value)
    const { close, open, show } = useToggle()
    const focussedIndex = ref(-1)
    const inputId = genHtmlId()
    const dropdown = ref(null)
    const target = ref(null)

    const focus = computed(() => search.value !== undefined)

    const throttleInput = throttle(() => {
      emit('update')
    }, throttleProp.value)

    const searchEmpty = computed(() =>
      userSearch.value
        ? !searchUserName.value ||
          (isString(searchUserName.value) && searchUserName.value.trim() === '')
        : !search.value ||
          (isString(search.value) && search.value.trim() === '')
    )

    watch(results, (val) => {
      if (val.length) {
        open()
      }
    })

    function updateInput(input) {
      const trimmed = input?.trim()
      if (trimmed !== search.value || (search.value && !!trimmed)) {
        if (userSearch.value) {
          setUserSearch(input)
        } else {
          setSearch(input, input)
        }
        throttleInput()
      }
    }

    function clear() {
      resetPagination()

      if (userSearch.value) {
        clearUserSearch()
      } else {
        clearSearch()
      }

      emit('update')
    }

    function select(item) {
      emit('selected:item', item)
      close()
    }

    function changeIndex(dir) {
      focussedIndex.value =
        (focussedIndex.value + dir + results.value.length) %
        results.value.length

      _scrollTo()
    }

    function handleKeyPress(e) {
      switch (e.keyCode) {
        case 9: //tab
          close()
          break

        case 27: //escape
          if (show.value) {
            e.preventDefault()
            e.stopPropagation()
            close()
          }
          break

        case 13: //return
          e.preventDefault()
          if (show.value) {
            if (focussedIndex.value >= 0) {
              select(results.value[focussedIndex.value].value)
            }
          } else {
            open()
          }
          break

        case 38: //up
          e.preventDefault()
          changeIndex(-1)
          break

        case 40: //down
          e.preventDefault()
          changeIndex(1)
          break
      }
    }

    function _scrollTo() {
      if (focussedIndex.value >= 0) {
        dropdown.value?.children[focussedIndex.value].scrollIntoView({
          block: 'nearest',
        })
      }
    }

    onClickOutside(target, close)

    return {
      handleKeyPress,
      searchUserName,
      focussedIndex,
      searchEmpty,
      updateInput,
      searchText,
      dropdown,
      inputId,
      search,
      select,
      target,
      clear,
      focus,
      close,
      open,
      show,
    }
  },
}
</script>
